import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { useLoaderData, useRevalidator } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import EventCards from 'src/components/ui/EventCards';
import TopControls from 'src/components/ui/TopControls';
import CompanyPage from 'src/pages/CompanyPage';
import EventFormCWithModals from 'src/components/ui/EventForm/connectors/EventFormWithModals';
import StatusPlate from 'src/components/ui/StatusPlate';
import RouterLink from 'src/components/ui/RouterLink';
import SortChipsCActivities from 'src/components/ui/SortChips/connectors/SortChipsCActivities';
import {
	getParentGroups,
	isCompanyGroupMember,
} from 'src/utils/mappers/companySideArea/groups.mapper';
import { parseFilters } from 'src/hooks/filters/useFilters';
import { mapActivityToEventFormState } from 'src/utils/mappers/eventForm/activityToForm.mapper';

const ACTIVITY_FILTER_CODES = ['createdBy', 'itemEntityType'];

const GroupMemberEventsNotice = ({ groups }) => {
	const [group] = groups;

	return (
		<Alert severity="info" variant="outlined">
			Создание новых событий недоступно — компания входит в группу.
			{group && (
				<>
					{' '}
					Создавайте события на странице группы{' '}
					<Link
						component={RouterLink}
						to={`/company/${group.id}`}
						underline="hover"
						fontWeight={600}
					>
						{group.name}
					</Link>
					.
				</>
			)}
		</Alert>
	);
};

GroupMemberEventsNotice.propTypes = {
	groups: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			name: PropTypes.string,
		}),
	).isRequired,
};

const CompanyPageRoute = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const isCreateForm = searchParams.get('create') === 'true';
	const editActivityId = searchParams.get('edit');
	const {
		company,
		eventFormData,
		eventCards,
		activities = [],
		activityMapperContext = {},
		activityFacets,
		statusPlate,
	} = useLoaderData();
	const revalidator = useRevalidator();

	const isGroupMember = isCompanyGroupMember(company);
	const parentGroups = isGroupMember ? getParentGroups(company) : [];

	const editFormState = useMemo(() => {
		if (!editActivityId) return null;

		const activity = activities.find(
			(entry) =>
				String(entry.id) === String(editActivityId) && entry.canUpdate,
		);

		if (!activity) return null;

		return mapActivityToEventFormState(activity, {
			...activityMapperContext,
			statusOptions: eventFormData?.status?.options ?? [],
		});
	}, [
		activities,
		activityMapperContext,
		editActivityId,
		eventFormData?.status?.options,
	]);

	const showEventFilters = useMemo(() => {
		if (eventCards.length > 0) return true;

		const filters = parseFilters(searchParams);

		return ACTIVITY_FILTER_CODES.some((code) => {
			const value = filters[code]?.eq;

			return Array.isArray(value) ? value.length > 0 : Boolean(value);
		});
	}, [eventCards.length, searchParams]);

	// Участник группы не создаёт новые события, но может править исходное (canUpdate).
	const canShowEventForm =
		isCreateForm && (!isGroupMember || Boolean(editFormState));

	useEffect(() => {
		if (!isGroupMember || !isCreateForm) return;
		// Не сбрасываем ?edit= — иначе «Исправить ошибку» сразу закрывается.
		if (editActivityId) return;

		const next = new URLSearchParams(searchParams);
		next.delete('create');
		setSearchParams(next, { replace: true });
	}, [
		isGroupMember,
		isCreateForm,
		editActivityId,
		searchParams,
		setSearchParams,
	]);

	useEffect(() => {
		if (!editActivityId || editFormState || !isCreateForm) return;

		const next = new URLSearchParams(searchParams);
		next.delete('edit');
		if (isGroupMember) {
			next.delete('create');
		}
		setSearchParams(next, { replace: true });
	}, [
		editActivityId,
		editFormState,
		isCreateForm,
		isGroupMember,
		searchParams,
		setSearchParams,
	]);

	const newEventButtonClickHandler = () => {
		if (isGroupMember) return;

		const next = new URLSearchParams(searchParams);
		next.delete('edit');
		next.set('create', 'true');
		setSearchParams(next, { replace: true });
	};

	const cancelHandler = () => {
		const next = new URLSearchParams(searchParams);
		next.delete('create');
		next.delete('edit');

		setSearchParams(next, { replace: true });
	};

	const handleEventSaved = async () => {
		cancelHandler();
		await revalidator.revalidate();
	};

	return (
		<CompanyPage
			EventFormRP={() => {
				if (!canShowEventForm) return;

				return (
					<EventFormCWithModals
						key={editFormState?.activityId || 'create'}
						{...eventFormData}
						comment={{
							placeholder: 'Комментарий',
							multiline: true,
							minRows: 4,
						}}
						cancelHandler={cancelHandler}
						onSuccess={handleEventSaved}
						activityId={editFormState?.activityId}
						initialComment={editFormState?.comment || ''}
						initialStatus={editFormState?.status || null}
						initialStatusReason={
							editFormState?.statusReason || null
						}
						initialStatusDate={editFormState?.statusDate || null}
						initialPayloadItems={editFormState?.payloadItems || []}
						submitLabel={
							editFormState
								? 'Сохранить изменения'
								: eventFormData.submitLabel
						}
					/>
				);
			}}
			EventCardsRP={() => (
				<EventCards
					wrap={{
						title: 'События',
						empty: 'Нет событий',
						SortChipsRP: showEventFilters
							? () => (
								<SortChipsCActivities
									facets={activityFacets}
								/>
							)
							: undefined,
					}}
					cards={eventCards}
					notice={
						isGroupMember ? (
							<GroupMemberEventsNotice groups={parentGroups} />
						) : null
					}
				/>
			)}
			TopControlsRP={() => (
				<TopControls
					addLabel={isGroupMember ? undefined : 'Новое событие'}
					disabled={isCreateForm}
					clickHandler={newEventButtonClickHandler}
					StatusPlateRP={() => <StatusPlate {...statusPlate} />}
				/>
			)}
		/>
	);
};

export default CompanyPageRoute;
